import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import userService, { User, CreateUserPayload, UpdateUserPayload } from '../../services/userService';
import companyService, { Company } from '../../services/companyService';
import ChangePasswordModal from '../../components/ChangePasswordModal';
import Swal from 'sweetalert2';

const UserManagement: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordChangeUser, setPasswordChangeUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit] = useState(10);
  const [formData, setFormData] = useState<CreateUserPayload>({
    name: '',
    email: '',
    role: 'user',
    permissions: [],
    companyId: '', // For admin to select company
  });

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      // BFF will determine which users to show based on the token (role and companyId)
      // Admin: all users
      // Manager/User: only users from their company
      
      const response = await userService.listUsers({
        search: searchTerm,
        page: currentPage,
        limit: limit,
      });
      
      const usersData = Array.isArray(response.data) ? response.data : [];
      console.log('Users loaded:', JSON.stringify(usersData.map(u => ({ name: u.name, company_id: u.company_id })), null, 2));
      setUsers(usersData);
      setTotal(response.total || 0);
      setTotalPages(response.totalPages || 1);
    } catch (error: any) {
      Swal.fire('Erro', 'Erro ao carregar usuários', 'error');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, limit]);

  const loadCompanies = useCallback(async () => {
    // Only admin needs to load companies
    if (currentUser?.role !== 'admin') return;
    
    try {
      const response = await companyService.listCompanies({ page: 1, limit: 1000 });
      const companiesData = response.data || [];
      setCompanies(companiesData);
      console.log('Companies loaded:', JSON.stringify(companiesData.map(c => ({ id: c.id, name: c.name })), null, 2));
    } catch (error) {
      console.error('Error loading companies:', error);
    }
  }, [currentUser]);

  useEffect(() => {
    if (currentUser) {
      loadUsers();
      loadCompanies();
    }
  }, [currentUser, loadUsers, loadCompanies]);

  const handleOpenModal = (user?: User) => {
    if (user) {
      setSelectedUser(user);
      setFormData({
        name: user.name,
        email: user.email,
        role: user.role,
        permissions: user.permissions,
        companyId: user.company_id || '',
      });
    } else {
      setSelectedUser(null);
      setFormData({
        name: '',
        email: '',
        role: 'user',
        permissions: [],
        companyId: '',
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedUser(null);
    setFormData({
      name: '',
      email: '',
      role: 'user',
      permissions: [],
      companyId: '',
    });
  };

  const getCompanyName = (company_id?: string) => {
    console.log(`Getting company name for company_id=${company_id}, companies count=${companies.length}`);
    if (!company_id) return 'Sistema';
    const company = companies.find(c => c.id === company_id);
    console.log(`Found company: ${company ? company.name : 'NOT FOUND'}`);
    return company ? company.name : 'Sistema';
  };  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = (): boolean => {
    if (!formData.name.trim()) {
      Swal.fire('Erro', 'Nome é obrigatório', 'error');
      return false;
    }
    if (!formData.email.trim()) {
      Swal.fire('Erro', 'E-mail é obrigatório', 'error');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      Swal.fire('Erro', 'E-mail inválido', 'error');
      return false;
    }
    if (!formData.role) {
      Swal.fire('Erro', 'Perfil é obrigatório', 'error');
      return false;
    }
    // Admin must select a company when creating users
    if (currentUser?.role === 'admin' && !selectedUser && !formData.companyId) {
      Swal.fire('Erro', 'Empresa é obrigatória', 'error');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    try {
      if (selectedUser) {
        // Update
        const updatePayload: UpdateUserPayload = {};
        if (formData.name !== selectedUser.name) updatePayload.name = formData.name;
        if (formData.email !== selectedUser.email) updatePayload.email = formData.email;
        if (formData.role !== selectedUser.role) updatePayload.role = formData.role;
        if (JSON.stringify(formData.permissions) !== JSON.stringify(selectedUser.permissions)) {
          updatePayload.permissions = formData.permissions;
        }

        await userService.updateUser(selectedUser.id, updatePayload);
        Swal.fire('Sucesso', 'Usuário atualizado com sucesso', 'success');
        loadUsers();
      } else {
        // Create - backend generates password
        const newUser = await userService.createUser(formData);
        
        // Display generated password with SweetAlert2
        await Swal.fire({
          title: 'Usuário Criado!',
          html: `
            <p>Usuário criado com sucesso.</p>
            <p><strong>E-mail:</strong> ${newUser.email}</p>
            <p><strong>Senha temporária:</strong></p>
            <div class="input-group mb-3">
              <input 
                type="text" 
                class="form-control" 
                value="${newUser.password}" 
                id="generated-password"
                readonly
              />
              <div class="input-group-append">
                <button 
                  class="btn btn-outline-secondary" 
                  type="button"
                  onclick="navigator.clipboard.writeText('${newUser.password}'); 
                    const btn = this;
                    btn.innerHTML = '<i class=\\'fas fa-check\\'></i> Copiado';
                    setTimeout(() => { btn.innerHTML = '<i class=\\'fas fa-copy\\'></i>'; }, 2000);"
                >
                  <i class="fas fa-copy"></i>
                </button>
              </div>
            </div>
            <p class="text-warning">
              <i class="fas fa-exclamation-triangle"></i>
              Esta senha será exibida apenas uma vez. Anote ou copie agora!
            </p>
          `,
          icon: 'success',
          confirmButtonText: 'Entendi',
          allowOutsideClick: false,
        });

        loadUsers();
      }
      handleCloseModal();
    } catch (error: any) {
      Swal.fire('Erro', error.response?.data?.error || 'Erro ao salvar usuário', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (user: User) => {
    const result = await Swal.fire({
      title: 'Confirmar exclusão?',
      text: `Deseja realmente excluir o usuário "${user.name}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sim, excluir',
      cancelButtonText: 'Cancelar',
    });

    if (result.isConfirmed) {
      try {
        await userService.deleteUser(user.id);
        loadUsers();
        Swal.fire('Excluído', 'Usuário excluído com sucesso', 'success');
      } catch (error: any) {
        Swal.fire('Erro', error.response?.data?.error || 'Erro ao excluir usuário', 'error');
      }
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1); // Reset to first page on search
  };

  const handleOpenPasswordModal = (user: User) => {
    setPasswordChangeUser(user);
    setShowPasswordModal(true);
  };

  const handleClosePasswordModal = () => {
    setPasswordChangeUser(null);
    setShowPasswordModal(false);
  };

  const handleChangePassword = async (currentPassword: string, newPassword: string) => {
    if (!passwordChangeUser) return;
    
    try {
      // Admin/manager não precisa passar senha atual ao trocar senha de outros
      await userService.changePassword(passwordChangeUser.id, newPassword);
      // Success handled in modal
    } catch (error: any) {
      throw new Error(error.message || 'Erro ao alterar senha');
    }
  };

  // Check if current user can change password of target user
  const canChangePassword = (targetUser: User): boolean => {
    if (!currentUser) return false;
    
    // User can change their own password
    if (currentUser.account_id === targetUser.id) return true;
    
    // Admin and manager can change other users' passwords
    if (currentUser.role === 'admin' || currentUser.role === 'manager') return true;
    
    return false;
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    pages.push(
      <li key="prev" className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
        <button
          className="page-link"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          <i className="fas fa-chevron-left"></i>
        </button>
      </li>
    );

    if (startPage > 1) {
      pages.push(
        <li key={1} className="page-item">
          <button className="page-link" onClick={() => handlePageChange(1)}>
            1
          </button>
        </li>
      );
      if (startPage > 2) {
        pages.push(
          <li key="ellipsis1" className="page-item disabled">
            <span className="page-link">...</span>
          </li>
        );
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <li key={i} className={`page-item ${currentPage === i ? 'active' : ''}`}>
          <button className="page-link" onClick={() => handlePageChange(i)}>
            {i}
          </button>
        </li>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(
          <li key="ellipsis2" className="page-item disabled">
            <span className="page-link">...</span>
          </li>
        );
      }
      pages.push(
        <li key={totalPages} className="page-item">
          <button className="page-link" onClick={() => handlePageChange(totalPages)}>
            {totalPages}
          </button>
        </li>
      );
    }

    pages.push(
      <li key="next" className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
        <button
          className="page-link"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          <i className="fas fa-chevron-right"></i>
        </button>
      </li>
    );

    return (
      <nav aria-label="Page navigation">
        <ul className="pagination pagination-sm mb-0">{pages}</ul>
      </nav>
    );
  };

  const getRoleBadge = (role: string) => {
    const badges: { [key: string]: string } = {
      admin: 'badge-danger',
      manager: 'badge-warning',
      user: 'badge-primary',
    };
    return badges[role] || 'badge-secondary';
  };

  return (
    <>
      <section className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1>Gerenciamento de Usuários</h1>
            </div>
            <div className="col-sm-6">
              <ol className="breadcrumb float-sm-right">
                <li className="breadcrumb-item"><a href="/dashboard">Home</a></li>
                <li className="breadcrumb-item active">Usuários</li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      <section className="content">
        <div className="container-fluid">
          <div className="card">
            <div className="card-header">
              <div className="row align-items-center">
                <div className="col-md-4">
                  <h3 className="card-title">
                    <i className="fas fa-users mr-2"></i>
                    Lista de Usuários
                  </h3>
                </div>
                <div className="col-md-4">
                  <div className="input-group input-group-sm">
                    <input
                      type="text"
                      className="form-control"
                      placeholder={currentUser?.role === 'admin' ? "Buscar por nome, email ou empresa..." : "Buscar usuário..."}
                      value={searchTerm}
                      onChange={(e) => handleSearch(e.target.value)}
                    />
                    <div className="input-group-append">
                      <span className="input-group-text">
                        <i className="fas fa-search"></i>
                      </span>
                    </div>
                  </div>
                </div>
                <div className="col-md-4 text-right">
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => handleOpenModal()}
                  >
                    <i className="fas fa-plus mr-1"></i>
                    Novo Usuário
                  </button>
                </div>
              </div>
            </div>
            <div className="card-body p-0">
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="sr-only">Carregando...</span>
                  </div>
                  <p className="mt-2 text-muted">Carregando usuários...</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-striped table-hover mb-0">
                    <thead className="bg-light">
                      <tr>
                        {currentUser?.role === 'admin' && <th>Empresa</th>}
                        <th>Nome</th>
                        <th>E-mail</th>
                        <th>Perfil</th>
                        <th className="text-center" style={{ width: '100px' }}>Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.length === 0 ? (
                        <tr>
                          <td colSpan={currentUser?.role === 'admin' ? 5 : 4} className="text-center py-4 text-muted">
                            <i className="fas fa-user-slash fa-2x mb-3 d-block"></i>
                            {searchTerm ? 'Nenhum usuário encontrado para sua busca' : 'Nenhum usuário cadastrado'}
                          </td>
                        </tr>
                      ) : (
                        users.map((user) => (
                          <tr key={user.id}>
                            {currentUser?.role === 'admin' && (
                              <td className="align-middle">
                                {getCompanyName(user.company_id)}
                              </td>
                            )}
                            <td className="align-middle">
                              <strong>{user.name}</strong>
                            </td>
                            <td className="align-middle">
                              <a href={`mailto:${user.email}`}>{user.email}</a>
                            </td>
                            <td className="align-middle">
                              <span className={`badge ${getRoleBadge(user.role)}`}>
                                {user.role === 'admin' ? 'Administrador' : user.role === 'manager' ? 'Gerente' : 'Usuário'}
                              </span>
                            </td>
                            <td className="text-center align-middle">
                              <div className="btn-group btn-group-sm">
                                <button
                                  className="btn btn-info"
                                  onClick={() => handleOpenModal(user)}
                                  title="Editar"
                                >
                                  <i className="fas fa-edit"></i>
                                </button>
                                {canChangePassword(user) && (
                                  <button
                                    className="btn btn-warning"
                                    onClick={() => handleOpenPasswordModal(user)}
                                    title="Alterar Senha"
                                  >
                                    <i className="fas fa-key"></i>
                                  </button>
                                )}
                                <button
                                  className="btn btn-danger"
                                  onClick={() => handleDelete(user)}
                                  title="Excluir"
                                >
                                  <i className="fas fa-trash"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            {!loading && users.length > 0 && (
              <div className="card-footer clearfix">
                <div className="row align-items-center">
                  <div className="col-md-6">
                    <small className="text-muted">
                      Mostrando {users.length} de {total} usuário(s)
                    </small>
                  </div>
                  <div className="col-md-6">
                    <div className="float-right">
                      {renderPagination()}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Modal */}
      {showModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <form onSubmit={handleSubmit}>
                <div className="modal-header">
                  <h4 className="modal-title">
                    {currentUser ? 'Editar Usuário' : 'Novo Usuário'}
                  </h4>
                  <button
                    type="button"
                    className="close"
                    onClick={handleCloseModal}
                  >
                    <span>&times;</span>
                  </button>
                </div>
                <div className="modal-body">
                  {currentUser?.role === 'admin' && !selectedUser && (
                    <div className="form-group">
                      <label>Empresa *</label>
                      <select
                        className="form-control"
                        name="companyId"
                        value={formData.companyId}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Selecione uma empresa</option>
                        {companies.map((company) => (
                          <option key={company.id} value={company.id}>
                            {company.name}
                          </option>
                        ))}
                      </select>
                      <small className="form-text text-muted">
                        Selecione a empresa para a qual o usuário será criado
                      </small>
                    </div>
                  )}
                  <div className="form-group">
                    <label>Nome *</label>
                    <input
                      type="text"
                      className="form-control"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>E-mail *</label>
                    <input
                      type="email"
                      className="form-control"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Perfil *</label>
                    <select
                      className="form-control"
                      name="role"
                      value={formData.role}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="user">Usuário</option>
                      <option value="manager">Gerente</option>
                      <option value="admin">Administrador</option>
                    </select>
                  </div>
                  {!currentUser && (
                    <div className="alert alert-info">
                      <i className="fas fa-info-circle"></i>
                      Uma senha será gerada automaticamente e exibida após a criação.
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-default"
                    onClick={handleCloseModal}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? 'Salvando...' : 'Salvar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      
      {/* Change Password Modal */}
      <ChangePasswordModal
        show={showPasswordModal}
        onClose={handleClosePasswordModal}
        onSubmit={handleChangePassword}
        userName={passwordChangeUser?.name || ''}
        requireCurrentPassword={false}
      />
    </>
  );
};

export default UserManagement;
